import { RefreshCw, X } from "lucide-react";
import { useEffect, useState } from "react";
import { watchForAppUpdate } from "../lib/app-update";
import { useAppPreferences } from "../lib/preferences";
import { Button } from "./ui/Button";

/** Offers a reload when a newly deployed version has taken over this tab. */
export function AppUpdateBanner({ reload = () => window.location.reload() }: { reload?: () => void }) {
  const { t } = useAppPreferences();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => watchForAppUpdate(() => setUpdateAvailable(true)), []);

  if (!updateAvailable || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-4 z-[110] mx-auto flex w-[calc(100%-2rem)] max-w-xl items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-panel)] p-3 shadow-lg sm:p-4"
    >
      <RefreshCw aria-hidden="true" className="h-5 w-5 shrink-0 text-[var(--color-signal)]" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[var(--color-text)]">{t("A new version of Gaamed is available")}</p>
        <p className="text-sm text-[var(--color-text-soft)]">{t("Reload to get the latest fixes. Unsaved changes on this page will be lost.")}</p>
      </div>
      <Button size="sm" onClick={reload}>
        {t("Reload")}
      </Button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t("Dismiss")}
        className="rounded-md p-1 text-[var(--color-muted)] hover:text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>
  );
}
